(() => {
  const DEFAULT_SCAN_API_URL = 'https://newscan.cloudstride.com/api/FlyingBook/AddScanInfo';
  const EMAIL_PROMPT = 'Enter your email address (for order matching)';
  const EMAIL_INVALID = 'Please enter a valid email address';

  function normalizeEmail(value) {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }

  function isValidEmail(value) {
    const email = normalizeEmail(value);
    const pattern = /^[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?(?:\.[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?)+$/i;
    return email.length > 0 && email.length <= 320 && pattern.test(email);
  }

  function buildScanPayload(email, scanId, runtime = {}) {
    return {
      email: normalizeEmail(email),
      scanid: scanId || '',
      pageUrl: runtime.pageUrl || '',
      timezone: runtime.timezone || '',
      screenWidth: runtime.screenWidth ?? null,
      screenHeight: runtime.screenHeight ?? null,
      devicePixelRatio: runtime.devicePixelRatio ?? null,
      platform: runtime.platform || null,
    };
  }

  function getRuntimeInfo() {
    return {
      pageUrl: window.location.href,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenWidth: window.screen ? window.screen.width : null,
      screenHeight: window.screen ? window.screen.height : null,
      devicePixelRatio: window.devicePixelRatio ? Number(window.devicePixelRatio) : null,
      platform: navigator.platform || null,
    };
  }

  function triggerScanWidget(widget) {
    if (!widget) return;
    try {
      const shadowButton = widget.shadowRoot ? widget.shadowRoot.querySelector('.container') : null;
      if (shadowButton && typeof shadowButton.click === 'function') {
        shadowButton.click();
        return;
      }
    } catch (error) {
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('Unable to trigger Volumental shadow button.', error);
      }
    }
    if (typeof widget.click === 'function') widget.click();
  }

  function wireEmailScanFlow({
    startButton,
    dialog,
    input,
    tip,
    submitButton,
    widget,
    status,
    apiUrl = DEFAULT_SCAN_API_URL,
    triggerScan = () => triggerScanWidget(widget),
    fetchImpl = typeof fetch === 'function' ? fetch.bind(globalThis) : null,
    runtimeInfo = typeof window !== 'undefined' ? getRuntimeInfo : () => ({}),
  }) {
    let email = '';
    let pendingScanId = '';
    let savedKey = '';

    const setTip = (message, isError = false) => {
      if (!tip) return;
      tip.textContent = message;
      tip.classList?.toggle('is-error', isError);
    };

    const setStatus = (message, isSuccess = false) => {
      if (!status) return;
      status.textContent = message;
      status.classList?.toggle('is-success', isSuccess);
    };

    const setEmail = (value) => {
      email = normalizeEmail(value);
      if (input) input.value = email;
    };

    const openEmailDialog = (scanId = '') => {
      pendingScanId = scanId || '';
      if (input) input.value = email;
      setTip(EMAIL_PROMPT);
      input?.removeAttribute('aria-invalid');
      if (dialog && typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
      setTimeout(() => input?.focus(), 0);
    };

    const closeEmailDialog = () => {
      pendingScanId = '';
      if (dialog && typeof dialog.close === 'function' && dialog.open) dialog.close();
    };

    const sendScanId = async (scanId) => {
      const currentScanId = scanId || '';
      const currentEmail = normalizeEmail(email);
      const requestKey = `${currentEmail}:${currentScanId}`;
      if (!currentScanId || !isValidEmail(currentEmail) || savedKey === requestKey) return;
      if (!fetchImpl) {
        setStatus('Your browser cannot submit scan results. Please try another browser.');
        return;
      }

      savedKey = requestKey;
      try {
        const response = await fetchImpl(apiUrl, {
          method: 'POST',
          headers: {'Content-Type': 'application/json; charset=utf-8'},
          body: JSON.stringify(buildScanPayload(currentEmail, currentScanId, runtimeInfo())),
        });
        const data = await response.json();
        const isSuccess = Boolean(data && data.errorCode == 0);
        setStatus(isSuccess ? (data.errorMsg || 'Scan information submitted successfully.') : ((data && data.errorMsg) || 'Failed to submit scan information.'), isSuccess);
      } catch (error) {
        savedKey = '';
        setStatus('Failed to submit scan information. Please try again.');
      }
    };

    const submitEmail = () => {
      const value = normalizeEmail(input?.value);
      if (!isValidEmail(value)) {
        input?.setAttribute('aria-invalid', 'true');
        setTip(value ? EMAIL_INVALID : EMAIL_PROMPT, true);
        input?.focus();
        return;
      }

      setEmail(value);
      input?.removeAttribute('aria-invalid');
      setTip('');
      const measuredScanId = pendingScanId;
      closeEmailDialog();
      if (measuredScanId) sendScanId(measuredScanId);
      else triggerScan();
    };

    startButton?.addEventListener('click', (event) => {
      event.preventDefault();
      if (email) triggerScan();
      else openEmailDialog();
    });
    submitButton?.addEventListener('click', submitEmail);
    input?.addEventListener('input', () => {
      input.removeAttribute('aria-invalid');
      setTip(input.value.trim() ? '' : EMAIL_PROMPT);
    });
    input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        submitEmail();
      }
    });
    widget?.addEventListener('volumental:on-opened', () => {
      if (!email) openEmailDialog();
    });
    widget?.addEventListener('volumental:on-measurement', (event) => {
      const scanId = event && event.detail ? event.detail.id : '';
      if (!scanId) return;
      if (!email) openEmailDialog(scanId);
      else return sendScanId(scanId);
    });

    return {openEmailDialog, sendScanId, setEmail, submitEmail};
  }

  function setupEmailScan(root) {
    const startButton = root.querySelector('[data-fizo-scan-start]');
    const dialog = root.querySelector('[data-fizo-scan-email-dialog]');
    const input = root.querySelector('[data-fizo-scan-email-input]');
    const tip = root.querySelector('[data-fizo-scan-email-tip]');
    const submitButton = root.querySelector('[data-fizo-scan-email-submit]');
    const widget = root.querySelector('[data-fizo-scan-widget]');
    const status = root.querySelector('[data-fizo-scan-status]');
    if (!startButton || !dialog || !input || !submitButton || !widget) return;

    wireEmailScanFlow({
      startButton,
      dialog,
      input,
      tip,
      submitButton,
      widget,
      status,
      apiUrl: root.dataset.scanApiUrl || DEFAULT_SCAN_API_URL,
    });
  }

  function initialize(root) {
    setupEmailScan(root);
    const steps = [...root.querySelectorAll('[data-scan-step]')];
    const mediaItems = [...root.querySelectorAll('[data-scan-stage-item]')];
    const badge = root.querySelector('[data-scan-stage-badge]');
    const progress = root.querySelector('[data-scan-stage-progress]');
    const sharedVideo = root.querySelector('[data-scan-shared-video] video');
    if (!steps.length) return;

    const overviewCards = [...root.querySelectorAll('.fizo-scan-landing__overview-card')];
    const isMobileViewport = window.matchMedia('(max-width: 989px)');
    const stageWrap = root.querySelector('[data-scan-stage-wrap]');

    const setupOverviewScrollState = () => {
      if (!overviewCards.length || !isMobileViewport.matches) return;

      let activeCard;
      const setClosestCardActive = () => {
        const viewportCenter = window.innerHeight / 2;
        const closestCard = overviewCards.reduce((closest, card) => {
          const cardRect = card.getBoundingClientRect();
          const cardDistance = Math.abs(cardRect.top + cardRect.height / 2 - viewportCenter);
          const closestRect = closest.getBoundingClientRect();
          const closestDistance = Math.abs(closestRect.top + closestRect.height / 2 - viewportCenter);
          return cardDistance < closestDistance ? card : closest;
        });

        if (closestCard === activeCard) return;
        activeCard = closestCard;
        overviewCards.forEach((card) => card.classList.toggle('is-active', card === activeCard));
      };

      const observer = new IntersectionObserver(setClosestCardActive, {
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });

      overviewCards.forEach((card) => observer.observe(card));
      window.addEventListener('resize', setClosestCardActive, {passive: true});
      setClosestCardActive();
    };

    const setupMobileStagePinning = () => {
      const footer = document.querySelector('footer.fizo-footer, .shopify-section-group-footer-group, footer');
      if (!stageWrap || !footer || !isMobileViewport.matches) return;

      let lastScrollY = window.scrollY;
      let scrollDirection = 'down';
      let isFooterVisible = false;
      const updateStageOffset = () => {
        const header = document.querySelector('.section-header');
        const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
        root.style.setProperty('--fizo-scan-stage-top', `${Math.ceil(headerBottom + 8)}px`);
        const stageRect = stageWrap.getBoundingClientRect();
        root.style.setProperty('--fizo-scan-stage-left', `${Math.round(stageRect.left)}px`);
        root.style.setProperty('--fizo-scan-stage-width', `${Math.round(stageRect.width)}px`);
        root.style.setProperty('--fizo-scan-stage-height', `${Math.round(stageRect.height)}px`);
      };

      const updatePinnedState = () => {
        const header = document.querySelector('.section-header');
        const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
        const shouldPin = !isFooterVisible && stageWrap.getBoundingClientRect().top <= headerBottom + 8;
        stageWrap.classList.toggle('is-stage-pinned', shouldPin);
      };

      const onScroll = () => {
        const nextScrollY = window.scrollY;
        if (nextScrollY !== lastScrollY) scrollDirection = nextScrollY > lastScrollY ? 'down' : 'up';
        lastScrollY = nextScrollY;
        updatePinnedState();
      };

      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          isFooterVisible = true;
        } else if (scrollDirection === 'up') {
          isFooterVisible = false;
        }
        updatePinnedState();
      }, {threshold: 0.05});

      window.addEventListener('scroll', onScroll, {passive: true});
      window.addEventListener('resize', () => {
        updateStageOffset();
        updatePinnedState();
      }, {passive: true});
      updateStageOffset();
      updatePinnedState();
      observer.observe(footer);
    };

    let activeIndex = 0;
    let queuedSharedIndex = null;
    let sharedVideoVisible = !('IntersectionObserver' in window);

    const setupSharedVideoVisibility = () => {
      const scanSteps = root.querySelector('[data-fizo-scan-steps]');
      if (!sharedVideo || !scanSteps || !('IntersectionObserver' in window)) {
        sharedVideoVisible = true;
        return;
      }

      const observer = new IntersectionObserver(([entry]) => {
        sharedVideoVisible = entry.isIntersecting && entry.intersectionRatio >= 0.1;
        if (sharedVideoVisible) sharedVideo.play().catch(() => {});
        else sharedVideo.pause();
      }, {threshold: [0, 0.1]});
      observer.observe(scanSteps);
    };

    const getSharedSegmentStarts = () => {
      const configuredStarts = steps.map((step) => Number(step.dataset.scanSegmentStart));
      const hasValidStarts = configuredStarts.every((start, index) => (
        Number.isFinite(start)
        && start >= 0
        && (index === 0 ? start === 0 : start > configuredStarts[index - 1])
        && (!Number.isFinite(sharedVideo?.duration) || start < sharedVideo.duration)
      ));

      if (hasValidStarts) return configuredStarts;
      if (!Number.isFinite(sharedVideo?.duration) || sharedVideo.duration <= 0) return [];
      return steps.map((_, index) => (sharedVideo.duration / steps.length) * index);
    };

    const playSharedSegment = (index) => {
      if (!sharedVideo) return;

      if (!Number.isFinite(sharedVideo.duration) || sharedVideo.duration <= 0) {
        queuedSharedIndex = index;
        return;
      }

      const segmentStarts = getSharedSegmentStarts();
      const segmentStart = segmentStarts[index];
      sharedVideo.currentTime = Math.min(segmentStart, Math.max(0, sharedVideo.duration - 0.05));
      if (sharedVideoVisible) sharedVideo.play().catch(() => {});
    };

    const setActive = (index, seekSharedVideo = true) => {
      activeIndex = Math.max(0, Math.min(index, steps.length - 1));
      steps.forEach((step, currentIndex) => {
        const isActive = currentIndex === activeIndex;
        step.classList.toggle('is-active', isActive);
        step.classList.toggle('is-complete', currentIndex < activeIndex);
        step.setAttribute('aria-selected', String(isActive));
      });
      mediaItems.forEach((media) => {
        const isActive = Number(media.dataset.scanStageItem) === activeIndex + 1;
        const video = media.querySelector('video');

        if (!sharedVideo) media.classList.toggle('is-active', isActive);
        if (!video) return;

        if (video === sharedVideo) return;
        video.pause();
        if (isActive) {
          video.currentTime = 0;
          video.play().catch(() => {});
        }
      });
      badge.textContent = `STEP ${activeIndex + 1} OF ${steps.length}`;
      progress.style.width = `${((activeIndex + 1) / steps.length) * 100}%`;
      if (seekSharedVideo) playSharedSegment(activeIndex);
    };

    if (sharedVideo) {
      sharedVideo.addEventListener('loadedmetadata', () => {
        playSharedSegment(queuedSharedIndex === null ? activeIndex : queuedSharedIndex);
        queuedSharedIndex = null;
      });
      sharedVideo.addEventListener('timeupdate', () => {
        if (!Number.isFinite(sharedVideo.duration) || sharedVideo.duration <= 0) return;

        const segmentStarts = getSharedSegmentStarts();
        let nextIndex = 0;
        for (let index = segmentStarts.length - 1; index >= 0; index -= 1) {
          if (sharedVideo.currentTime >= segmentStarts[index]) {
            nextIndex = index;
            break;
          }
        }

        if (nextIndex !== activeIndex) setActive(nextIndex, false);
      });
    }

    mediaItems.forEach((media, index) => {
      const video = media.querySelector('video');
      if (!video || video === sharedVideo) return;

      video.addEventListener('ended', () => {
        if (index === activeIndex && activeIndex < steps.length - 1) setActive(activeIndex + 1);
      });
    });

    steps.forEach((step, index) => step.addEventListener('click', () => setActive(index)));
    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') setActive(activeIndex + 1);
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') setActive(activeIndex - 1);
    });
    setupSharedVideoVisibility();
    setActive(activeIndex);
    setupOverviewScrollState();
    setupMobileStagePinning();
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {buildScanPayload, isValidEmail, normalizeEmail, wireEmailScanFlow};
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', () => document.querySelectorAll('[data-fizo-scan-landing]').forEach(initialize));
  }
})();
